import { experimentationFeatureFlagsEntitySchemaV1 } from '../__generated__';
import { deriveVariant, featureFlagFilters } from './utils';
/**
 * Creates a Snowplow context array for feature flags with variant information.
 *
 * This centralized utility:
 * - Uses LaunchDarkly's variationDetail to get variant information when available
 * - Falls back to deriving variants from flag values when ldClient is not yet initialized
 * - Derives variant labels (control, variant_1, variant_2, etc.)
 * - Supports configurable filtering
 * - Returns an empty array if no flags to include
 *
 * @param options Configuration options
 * @returns Array containing feature flags context object, or empty array if no flags to include
 *
 * @example
 * ```tsx
 * import { useLDClient } from 'launchdarkly-react-client-sdk';
 * import { createFeatureFlagContext, featureFlagFilters } from '@montugroup/data-collection';
 *
 * const ldClient = useLDClient();
 * const flags = useFlags();
 *
 * const flagContext = createFeatureFlagContext({
 *   ldClient,
 *   flags,
 *   filter: featureFlagFilters.all // or experimentOnly, v3Only, or custom
 * });
 *
 * <Snowplow
 *   pageViewContext={flagContext}
 *   {...otherProps}
 * />
 * ```
 */
export function createFeatureFlagContext(options) {
    const { ldClient, flags, filter } = options;
    // Default filter: include all flags
    const shouldIncludeFlag = filter || featureFlagFilters.all;
    const flagKeys = Object.keys(flags);
    const fallback = ldClient ? 'default' : 'unavailable';
    const processedFlags = flagKeys
        .filter((key) => shouldIncludeFlag(key, flags[key]))
        .map((key) => {
        const value = flags[key];
        const variationIndex = ldClient?.variationDetail(key, value).variationIndex;
        return {
            name: key,
            value: JSON.stringify(value),
            variant: deriveVariant({ value, variationIndex, fallback }),
            vendor: 'launchdarkly',
        };
    });
    // Return empty array if no flags to include
    if (processedFlags.length === 0) {
        return [];
    }
    const flagsEntity = {
        flags: processedFlags,
    };
    return [
        {
            schema: experimentationFeatureFlagsEntitySchemaV1,
            data: flagsEntity,
        },
    ];
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiY3JlYXRlRmVhdHVyZUZsYWdDb250ZXh0LmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2xpYi9mZWF0dXJlLWZsYWdzL2NyZWF0ZUZlYXR1cmVGbGFnQ29udGV4dC50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFFQSxPQUFPLEVBQUUseUNBQXlDLEVBQUUsTUFBTSxrQkFBa0IsQ0FBQztBQUU3RSxPQUFPLEVBQUUsYUFBYSxFQUFFLGtCQUFrQixFQUFFLE1BQU0sU0FBUyxDQUFDO0FBNkI1RDs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7R0FnQ0c7QUFDSCxNQUFNLFVBQVUsd0JBQXdCLENBQ3RDLE9BQXdDO0lBRXhDLE1BQU0sRUFBRSxRQUFRLEVBQUUsS0FBSyxFQUFFLE1BQU0sRUFBRSxHQUFHLE9BQU8sQ0FBQztJQUU1QyxvQ0FBb0M7SUFDcEMsTUFBTSxpQkFBaUIsR0FBRyxNQUFNLElBQUksa0JBQWtCLENBQUMsR0FBRyxDQUFDO0lBRTNELE1BQU0sUUFBUSxHQUFHLE1BQU0sQ0FBQyxJQUFJLENBQUMsS0FBSyxDQUFDLENBQUM7SUFDcEMsTUFBTSxRQUFRLEdBQUcsUUFBUSxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUMsQ0FBQyxDQUFDLGFBQWEsQ0FBQztJQUV0RCxNQUFNLGNBQWMsR0FBRyxRQUFRO1NBQzVCLE1BQU0sQ0FBQyxDQUFDLEdBQUcsRUFBRSxFQUFFLENBQUMsaUJBQWlCLENBQUMsR0FBRyxFQUFFLEtBQUssQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDO1NBQ25ELEdBQUcsQ0FBQyxDQUFDLEdBQUcsRUFBRSxFQUFFO1FBQ1gsTUFBTSxLQUFLLEdBQUcsS0FBSyxDQUFDLEdBQUcsQ0FBQyxDQUFDO1FBQ3pCLE1BQU0sY0FBYyxHQUFHLFFBQVEsRUFBRSxlQUFlLENBQUMsR0FBRyxFQUFFLEtBQUssQ0FBQyxDQUFDLGNBQWMsQ0FBQztRQUU1RSxPQUFPO1lBQ0wsSUFBSSxFQUFFLEdBQUc7WUFDVCxLQUFLLEVBQUUsSUFBSSxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUM7WUFDNUIsT0FBTyxFQUFFLGFBQWEsQ0FBQyxFQUFFLEtBQUssRUFBRSxjQUFjLEVBQUUsUUFBUSxFQUFFLENBQUM7WUFDM0QsTUFBTSxFQUFFLGNBQWM7U0FDdkIsQ0FBQztJQUNKLENBQUMsQ0FBQyxDQUFDO0lBRUwsNENBQTRDO0lBQzVDLElBQUksY0FBYyxDQUFDLE1BQU0sS0FBSyxDQUFDLEVBQUUsQ0FBQztRQUNoQyxPQUFPLEVBQUUsQ0FBQztJQUNaLENBQUM7SUFFRCxNQUFNLFdBQVcsR0FBNEM7UUFDM0QsS0FBSyxFQUFFLGNBQWM7S0FDdEIsQ0FBQztJQUVGLE9BQU87UUFDTDtZQUNFLE1BQU0sRUFBRSx5Q0FBeUM7WUFDakQsSUFBSSxFQUFFLFdBQVc7U0FDbEI7S0FDRixDQUFDO0FBQ0osQ0FBQyJ9